// src/pages/Chat.jsx
import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { sendMessageToAI } from '../api';
import createTripPrompt from '../utils/createPrompt';
import { generateItineraryPDF } from '../utils/pdfGenerator';
import { useHistoryApi } from '../api/history';
import logo from '../assets/vertikalogo.jpg';

import ChatHeader       from '../components/ChatHeader';
import SetupScreen      from '../components/SetupScreen';
import LoaderScreen     from '../components/LoaderScreen';
import ItineraryScreen  from '../components/ItineraryScreen';
import ErrorScreen      from '../components/ErrorScreen';
import Layout           from '../components/Layout';

const SUMMARY_STEP_INDEX = 11;

export default function Chat(content, fileName = 'itinerary.pdf') {
  const { id } = useParams();
  const navigate = useNavigate();

  const [tripData, setTripData]       = useState(null);
  const [initialStep, setInitialStep] = useState(0);
  const [loading, setLoading]         = useState(false);
  const [loaderIndex, setLoaderIndex] = useState(0);
  const [percent, setPercent]         = useState(0);
  const [finalizing, setFinalizing]   = useState(false);
  const [itinerary, setItinerary]     = useState('');
  const [error, setError]             = useState(false);
  const itineraryRef                  = useRef(null);

  const { saveHistory, fetchHistoryList } = useHistoryApi();
  const [historyList, setHistoryList] = useState([]);
  const hasSavedHistory = useRef(false);
  const loaderMsgInterval = useRef(null);
  const loaderPctInterval = useRef(null);

  // fetch history list
  useEffect(() => {
    let active = true;
    fetchHistoryList()
      .then(data => active && setHistoryList(Array.isArray(data) ? data : []))
      .catch(console.error);
    return () => { active = false; };
  }, []);

  // loader message rotation
  useEffect(() => {
    if (!loading) return;
    loaderMsgInterval.current = setInterval(() => {
      setLoaderIndex(i => (i + 1) % 10);
    }, 3600);
    return () => clearInterval(loaderMsgInterval.current);
  }, [loading]);

  // loader percentage animation
  useEffect(() => {
    if (!loading) return;
    let curr = 0;
    const maxPct = 80;
    const totalMs = 180000; // 3 mins
    const stepMs = 1000;
    const inc = maxPct / (totalMs / stepMs);
    setPercent(0);
    loaderPctInterval.current = setInterval(() => {
      curr = Math.min(curr + inc, maxPct);
      setPercent(curr);
      if (curr >= maxPct) clearInterval(loaderPctInterval.current);
    }, stepMs);
    return () => clearInterval(loaderPctInterval.current);
  }, [loading]);

  // save and refresh history once
  useEffect(() => {
    if (!itinerary || !tripData || hasSavedHistory.current) return;
    hasSavedHistory.current = true;
    (async () => {
      try {
        await saveHistory({ chatId: id, destination: tripData.destination, response: itinerary });
        const updated = await fetchHistoryList();
        if (Array.isArray(updated)) setHistoryList(updated);
      } catch (e) {
        console.error(e);
      }
    })();
  }, [itinerary, tripData, id]);

  const handleTripInfoSubmit = async (data) => {
    setTripData(data);
    setLoading(true);
    setError(false);
    setFinalizing(false);
    setPercent(0);
    setLoaderIndex(0);

    try {
      const resp = await sendMessageToAI(createTripPrompt(data));
      // clear loader animations
      clearInterval(loaderMsgInterval.current);
      clearInterval(loaderPctInterval.current);
      // finalize
      setFinalizing(true);
      setPercent(90);
      setTimeout(() => {
        setPercent(100);
        setLoading(false);
        setFinalizing(false);
        setItinerary(resp);
      }, 2000);
    } catch {
      clearInterval(loaderMsgInterval.current);
      clearInterval(loaderPctInterval.current);
      setError(true);
      setLoading(false);
      setFinalizing(false);
    }
  };

  const makeFilename = (dest) => {
    if (!dest) return 'itinerary.pdf';
    return dest.trim().replace(/[^\w\s]/g, '').replace(/\s+/g, '_').toLowerCase() + '.pdf';
  };

  const handleDownload = () => {
    if (itineraryRef.current) {
      generateItineraryPDF(itineraryRef.current, logo, makeFilename(tripData.destination));
    }
  };

  const backToSummary = () => {
    setTripData(null);
    setInitialStep(SUMMARY_STEP_INDEX);
  };

  const sidebarContent = (
    <div className="h-full flex flex-col">
      <h2 className="px-4 py-3 text-lg font-semibold border-b">Itinerary History</h2>
      <div className="flex-1 overflow-y-auto">
        {historyList.map(item => {
          const isActive = item.chatId === id;
          return (
            <div
              key={item.chatId}
              onClick={() => navigate(`/history/${item.chatId}`)}
              className={`px-4 py-3 cursor-pointer border-b last:border-0 ${isActive ? 'bg-blue-100 font-medium' : 'hover:bg-gray-100'}`}
            >
              <div className="text-sm">{item.destination}</div>
              <div className="text-xs text-gray-500">{new Date(item.createdAt).toLocaleString()}</div>
            </div>
          );
        })}
      </div>
    </div>
  );

  return (
    <Layout sidebar={sidebarContent}>
      <div className="relative min-h-screen w-full bg-gradient-to-br from-blue-50 to-green-50">
        <div className="relative z-10 flex flex-col items-center justify-center min-h-screen px-4 py-10">
          <div className="bg-white bg-opacity-95 shadow-2xl rounded-2xl w-full max-w-3xl flex flex-col overflow-hidden">
            <ChatHeader hasItinerary={!loading && !!tripData && !error} onDownload={handleDownload} />
            {error ? (
              <ErrorScreen onBack={backToSummary} />
            ) : !tripData ? (
              <SetupScreen onSubmit={handleTripInfoSubmit} initialStep={initialStep} />
            ) : loading ? (
              <LoaderScreen index={loaderIndex} percent={percent} finalizing={finalizing} />
            ) : (
              <ItineraryScreen ref={itineraryRef} content={itinerary} onDownload={handleDownload} />
            )}
          </div>
        </div>
      </div>
    </Layout>
  );
}
